import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useI18n, isTranslatableRoute } from "@/i18n";
import api from "@/lib/api";
import PRETRANSLATIONS from "@/i18n/pretranslations.json";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "SVG", "PATH"]);
const cache = {};            // `${lang}:${english}` -> translated
const inflight = new Set();  // english strings currently being fetched

// Seed the cache from build-time pre-translations so the first language switch
// is instant. Live AI translation still runs for anything not covered here.
(function seedPretranslations() {
  try {
    for (const [lang, map] of Object.entries(PRETRANSLATIONS || {})) {
      for (const [en, tr] of Object.entries(map || {})) {
        cache[`${lang}:${en}`] = tr;
      }
    }
  } catch (e) {}
})();

function loadCache() {
  try {
    const raw = localStorage.getItem("i18n_cache");
    if (raw) Object.assign(cache, JSON.parse(raw));
  } catch (e) {}
}
let saveTimer = null;
function saveCache() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    try { localStorage.setItem("i18n_cache", JSON.stringify(cache)); } catch (e) {}
  }, 800);
}
loadCache();

function shouldSkip(node) {
  let el = node.parentElement;
  while (el) {
    if (SKIP_TAGS.has(el.tagName)) return true;
    if (el.hasAttribute && el.hasAttribute("data-no-translate")) return true;
    el = el.parentElement;
  }
  return false;
}

export default function GlobalTranslator() {
  const { lang } = useI18n();
  const loc = useLocation();
  // node -> { en: <english source>, out: <last value we set> }
  const meta = useRef(new WeakMap());
  const applying = useRef(false);
  const observer = useRef(null);
  const langRef = useRef(lang);
  // Doctor/Admin/Support consoles are never translated → force English there.
  const effLang = isTranslatableRoute(loc.pathname) ? lang : "en";
  langRef.current = effLang;

  useEffect(() => {
    const collect = () => {
      const out = [];
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
        acceptNode(n) {
          if (!n.nodeValue || !n.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          if (shouldSkip(n)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      let n;
      while ((n = walker.nextNode())) out.push(n);
      return out;
    };

    const sourceOf = (n) => {
      const m = meta.current.get(n);
      // If React re-rendered this node, its current value differs from what we set.
      if (m && n.nodeValue === m.out) return m.en;
      // Otherwise the current text is a fresh English source.
      const en = n.nodeValue;
      meta.current.set(n, { en, out: en });
      return en;
    };

    const apply = (L) => {
      applying.current = true;
      try {
        for (const n of collect()) {
          const en = sourceOf(n);
          const trimmed = en.trim();
          const lead = en.slice(0, en.length - en.trimStart().length);
          const trail = en.slice(en.trimEnd().length);
          let target = en;
          if (L !== "en") {
            const t = cache[`${L}:${trimmed}`];
            target = t ? lead + t + trail : en;
          }
          if (n.nodeValue !== target) n.nodeValue = target;
          meta.current.set(n, { en, out: target });
        }
      } finally {
        applying.current = false;
      }
    };

    const fetchMissing = async (L) => {
      if (L === "en") { apply(L); return; }
      const need = new Set();
      for (const n of collect()) {
        const en = sourceOf(n).trim();
        if (en && !cache[`${L}:${en}`] && !inflight.has(`${L}:${en}`)) need.add(en);
      }
      apply(L); // apply whatever is already cached immediately
      const list = [...need];
      if (!list.length) return;
      list.forEach((t) => inflight.add(`${L}:${t}`));
      const CHUNK = 40;
      for (let i = 0; i < list.length; i += CHUNK) {
        const chunk = list.slice(i, i + CHUNK);
        try {
          const { data } = await api.post("/i18n/translate", { texts: chunk, lang: L });
          (data.translations || []).forEach((tr, j) => { cache[`${L}:${chunk[j]}`] = tr; });
          saveCache();
        } catch (e) {}
        chunk.forEach((t) => inflight.delete(`${L}:${t}`));
        if (langRef.current === L) apply(L);
      }
    };

    // Debounced re-apply on DOM mutations from React renders.
    let debounce = null;
    const onMutate = () => {
      if (applying.current) return;
      clearTimeout(debounce);
      debounce = setTimeout(() => fetchMissing(langRef.current), 120);
    };

    observer.current = new MutationObserver(onMutate);
    observer.current.observe(document.body, { childList: true, subtree: true, characterData: true });

    fetchMissing(effLang);

    return () => { observer.current && observer.current.disconnect(); clearTimeout(debounce); };
  }, [lang, loc.pathname]);

  return null;
}
